import { Navigate, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import CheckIcon from '@mui/icons-material/Check';
import { MembershipShimmer } from '../components/shimmer';
import {
  useGetMembershipSettingsQuery,
  useListMembershipPlansQuery,
} from '../store/siteContentApi';
import { ROUTES } from '../constants/routes';

// Build the CTA target based on admin-configured action.
const ctaHandler = (plan, navigate) => () => {
  switch (plan.cta_action) {
    case 'external':
      if (plan.cta_url) window.open(plan.cta_url, '_blank', 'noopener,noreferrer');
      return;
    case 'support':
      navigate(ROUTES.SUPPORT);
      return;
    case 'none':
      return;
    case 'login':
    default:
      navigate(ROUTES.LOGIN);
  }
};

const Membership = () => {
  const navigate = useNavigate();
  const { data: settings, isLoading: settingsLoading } = useGetMembershipSettingsQuery();
  const { data: plans = [], isLoading: plansLoading } = useListMembershipPlansQuery();

  // Gate the whole page behind the admin feature flag. While the settings
  // request is in flight, render a loading state (not the full page) so
  // users don't see the plans flash if the feature is off.
  if (settingsLoading) {
    return (
      <Box sx={{ bgcolor: 'var(--forest)', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: 'var(--sage-l)' }} />
      </Box>
    );
  }
  if (settings && !settings.is_enabled) {
    return <Navigate to={ROUTES.HOME} replace />;
  }

  return (
    <Box sx={{ bgcolor: 'var(--forest)', color: '#fff', minHeight: '100vh' }}>
      {/* Hero + plans in a single forest section */}
      <Container
        maxWidth="xl"
        sx={{
          maxWidth: 1200,
          px: { xs: 2, md: 4 },
          pt: { xs: 10, md: 14 },
          pb: { xs: 8, md: 12 },
        }}
      >
        {/* Centered header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1.25,
              mb: 2,
              lineHeight: 1,
            }}
          >
            <Box sx={{ width: 24, height: '1.5px', bgcolor: 'var(--sage-l)' }} />
            <Typography
              component="span"
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: 'var(--sage-l)',
                lineHeight: 1,
              }}
            >
              {settings?.hero_eyebrow || 'TrustLab Membership'}
            </Typography>
            <Box sx={{ width: 24, height: '1.5px', bgcolor: 'var(--sage-l)' }} />
          </Box>

          <Typography
            className="serif"
            sx={{
              fontSize: { xs: 28, sm: 32, md: 40, lg: 46 },
              fontWeight: 500,
              color: '#fff',
              lineHeight: 1.15,
              mb: 2,
            }}
          >
            {settings?.hero_title || 'One membership. Your health, all year.'}
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: 13, md: 15 },
              color: 'rgba(255,255,255,0.55)',
              fontWeight: 300,
              maxWidth: 560,
              mx: 'auto',
              lineHeight: 1.7,
            }}
          >
            {settings?.hero_subtitle || 'Stop paying per test. Get your complete annual checkup plus unlimited essential tests — for one flat price that pays for itself in weeks.'}
          </Typography>
        </Box>

        {/* Pricing cards — loading / empty states */}
        {plansLoading ? (
          <MembershipShimmer count={3} />
        ) : plans.length === 0 ? (
          <Typography sx={{ textAlign: 'center', fontSize: 14, color: 'rgba(255,255,255,0.5)', py: 6 }}>
            No membership plans available right now.
          </Typography>
        ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: plans.length === 1 ? '1fr' : plans.length === 2 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
            },
            gap: { xs: 2, md: 2.5 },
            width: '100%',
            alignItems: 'stretch',
            maxWidth: plans.length < 3 ? 800 : 'none',
            mx: 'auto',
          }}
        >
          {plans.map((plan) => {
            const featured = plan.featured;
            return (
              <Box
                key={plan.tier}
                sx={{
                  bgcolor: featured ? 'rgba(61,122,82,0.15)' : 'rgba(255,255,255,0.03)',
                  border: '1.5px solid',
                  borderColor: featured ? 'rgba(106,174,128,0.5)' : 'rgba(255,255,255,0.1)',
                  borderRadius: '20px',
                  p: { xs: 3, md: 3.5 },
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s',
                  '&:hover': {
                    borderColor: featured ? 'var(--sage-l)' : 'rgba(255,255,255,0.2)',
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                {featured && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -12,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      bgcolor: 'var(--amber)',
                      color: '#fff',
                      fontSize: 10,
                      fontWeight: 800,
                      letterSpacing: '0.12em',
                      textTransform: 'uppercase',
                      borderRadius: 100,
                      px: 1.5,
                      py: 0.625,
                      whiteSpace: 'nowrap',
                      lineHeight: 1,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    <Box component="span" sx={{ lineHeight: 1 }}>★</Box>
                    <Box component="span" sx={{ lineHeight: 1 }}>Most Popular</Box>
                  </Box>
                )}

                {/* Tier eyebrow */}
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 2,
                    lineHeight: 1,
                  }}
                >
                  <Box sx={{ width: 18, height: '1.5px', bgcolor: 'var(--sage-l)', flexShrink: 0 }} />
                  <Typography
                    component="span"
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: '0.18em',
                      textTransform: 'uppercase',
                      color: 'var(--sage-l)',
                      lineHeight: 1,
                    }}
                  >
                    {plan.title || plan.tier}
                  </Typography>
                </Box>

                {/* Price */}
                {plan.price != null ? (
                  <Typography
                    className="serif"
                    sx={{
                      fontSize: { xs: 36, md: 44 },
                      fontWeight: 700,
                      color: '#fff',
                      lineHeight: 1,
                      textAlign: 'center',
                      mb: 0.5,
                    }}
                  >
                    {plan.price_prefix || '₹'}{Number(plan.price).toLocaleString()}
                  </Typography>
                ) : (
                  <Typography
                    className="serif"
                    sx={{
                      fontSize: { xs: 32, md: 40 },
                      fontWeight: 700,
                      color: '#fff',
                      lineHeight: 1,
                      textAlign: 'center',
                      mb: 0.5,
                    }}
                  >
                    Custom
                  </Typography>
                )}

                {/* Tagline */}
                <Typography
                  sx={{
                    fontSize: 12,
                    color: 'rgba(255,255,255,0.5)',
                    textAlign: 'center',
                    mb: 3,
                    lineHeight: 1,
                  }}
                >
                  {plan.tagline}
                </Typography>

                {/* Divider */}
                <Box
                  sx={{
                    height: '1px',
                    bgcolor: 'rgba(255,255,255,0.08)',
                    mb: 2.25,
                  }}
                />

                {/* Perks */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, flex: 1, mb: 2.5 }}>
                  {(plan.perks || []).map((perk, i) => (
                    <Box
                      key={`${plan.tier}-${i}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 18,
                          height: 18,
                          borderRadius: '50%',
                          bgcolor: 'rgba(106,174,128,0.2)',
                          color: 'var(--sage-l)',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          mt: '2px',
                          lineHeight: 1,
                        }}
                      >
                        <CheckIcon sx={{ fontSize: 12, display: 'block' }} />
                      </Box>
                      <Typography
                        component="span"
                        sx={{
                          fontSize: 12.5,
                          lineHeight: 1.5,
                          color: 'rgba(255,255,255,0.75)',
                          fontWeight: 400,
                        }}
                      >
                        {perk}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* CTA button — click handler driven by admin-configured
                    cta_action (login / support / external / none) */}
                <Button
                  onClick={ctaHandler(plan, navigate)}
                  disabled={plan.cta_action === 'none'}
                  sx={{
                    width: '100%',
                    borderRadius: 100,
                    fontSize: 13,
                    fontWeight: 700,
                    textTransform: 'none',
                    py: 1.125,
                    ...(featured
                      ? {
                          bgcolor: 'var(--amber)',
                          color: '#fff',
                          '&:hover': { bgcolor: '#C77C1D' },
                        }
                      : plan.price == null
                      ? {
                          bgcolor: 'var(--ink)',
                          color: '#fff',
                          border: '1.5px solid rgba(255,255,255,0.2)',
                          '&:hover': { bgcolor: '#000', borderColor: '#fff' },
                        }
                      : {
                          bgcolor: 'transparent',
                          color: 'var(--sage-l)',
                          border: '1.5px solid rgba(106,174,128,0.4)',
                          '&:hover': {
                            bgcolor: 'rgba(106,174,128,0.1)',
                            borderColor: 'var(--sage-l)',
                          },
                        }),
                  }}
                >
                  {plan.cta_label || 'Join Now'}
                </Button>
              </Box>
            );
          })}
        </Box>
        )}
      </Container>
    </Box>
  );
};

export default Membership;
